import React, { useState, useEffect } from 'react';
import { SectionInstance, ComponentSchema, ComponentCategory } from './types';
import ControlRenderer from './ControlRenderer';
import { RefreshCw, Trash2 } from 'lucide-react';
import axios from 'axios';
import type { SiteComponentFilter } from '../../services/api';

// Host-styled components take the imported page's own typography; the backend strips their
// section_* keys, so the generic Section Styling block would be a lie (imported-site-deep-insert-zones).
export const HOST_STYLED = new Set(['content_host_text']);

interface Props {
  section: SectionInstance | null;
  siteProjectId: string;
  onControlChange: (sectionId: string, controlKey: string, value: any) => void;
  onDeleteSection: (sectionId: string) => void;
  onRegenerateSection: (sectionId: string) => void;
  /** Brand logo assets from the linked brand guide, if any. */
  brandLogos?: { label: string; url: string }[];
  /** When true, the panel is hosted inside a shared sidebar wrapper — suppress own width/border/height. */
  embedded?: boolean;
  /** AI "regenerate section" assumes a component-based site_document. Imported static sites have none,
   *  so the editor passes false to hide the action. Defaults true (existing Site Builder behaviour). */
  allowRegenerate?: boolean;
  /** `?for=` used for the schema lookup. The unfiltered catalogue excludes imported_only schemas,
   *  so the imported editor passes "imported_all". Omit for the component Site Builder. */
  schemaFor?: SiteComponentFilter;
}

const getApiBase = () =>
  process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:8001`;

const PropertiesPanel: React.FC<Props> = ({ section, siteProjectId, onControlChange, onDeleteSection, onRegenerateSection, brandLogos = [], embedded = false, allowRegenerate = true, schemaFor }) => {
  const [schema, setSchema] = useState<ComponentSchema | null>(null);
  const [allSchemas, setAllSchemas] = useState<Record<string, ComponentSchema>>({});
  const [regenerating, setRegenerating] = useState(false);

  // Load schemas once
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    axios.get(`${getApiBase()}/site-builder/components`, {
      headers: { Authorization: `Bearer ${token}` },
      params: schemaFor ? { for: schemaFor } : undefined,
    }).then(res => {
      const map: Record<string, ComponentSchema> = {};
      (res.data.categories || []).forEach((cat: ComponentCategory) => {
        cat.components.forEach(c => { map[c.id] = c; });
      });
      setAllSchemas(map);
    }).catch(() => {});
  }, [schemaFor]);

  // Update schema when section changes
  useEffect(() => {
    if (section && allSchemas[section.component_id]) {
      setSchema(allSchemas[section.component_id]);
    } else {
      setSchema(null);
    }
  }, [section, allSchemas]);

  if (!section) {
    return (
      <div style={{
        ...(embedded ? {} : { width: 280, borderLeft: '1px solid rgba(0,0,0,0.08)' }),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#94a3b8', fontSize: '0.875rem', padding: 24, textAlign: 'center',
        minHeight: 80,
      }}>
        Select a section to edit its properties
      </div>
    );
  }

  const hostStyled = HOST_STYLED.has(section.component_id) || !!schema?.imported_only;

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      onRegenerateSection(section.section_id);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div style={{
      ...(embedded ? {} : { width: 280, borderLeft: '1px solid rgba(0,0,0,0.08)', height: '100%', overflow: 'hidden' }),
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{schema?.name || section.component_id}</div>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{section.component_id}</div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {allowRegenerate && (
            <button onClick={handleRegenerate} disabled={regenerating} title="Regenerate with AI" style={{
              border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: '#64748b',
            }}>
              <RefreshCw size={16} className={regenerating ? 'spin' : ''} />
            </button>
          )}
          <button onClick={() => onDeleteSection(section.section_id)} title="Delete section" style={{
            border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: '#ef4444',
          }}>
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      {/* Controls */}
      <div style={{ ...(embedded ? {} : { flex: 1, overflowY: 'auto' }), padding: 16, minWidth: 0, overflow: 'hidden' }}>

        {/* Schema controls — component-specific fields */}
        {schema ? (
          Object.entries(schema.controls).map(([key, controlDef]) => {
            const currentVariant = section.controls.variant || '';
            // Hide component-level gradient controls (handled universally below)
            if (['gradient_from', 'gradient_to', 'gradient_direction'].includes(key)) return null;
            // Hide background_image when not on an image variant
            if (key === 'background_image' && currentVariant && !currentVariant.includes('image')) return null;
            // Conditional visibility via visible_when
            if (controlDef.visible_when) {
              const visible = Object.entries(controlDef.visible_when).every(([k, v]) => {
                const cur = section.controls[k];
                return Array.isArray(v) ? v.includes(cur) : cur === v;
              });
              if (!visible) return null;
            }
            return (
              <ControlRenderer
                // Keyed per SECTION too: a stateful control (the rich-text editor's undo history)
                // must never carry over when the panel switches to another section of the same type.
                key={`${section.section_id}:${key}`}
                controlKey={key}
                controlDef={controlDef}
                value={section.controls[key]}
                onChange={(value) => onControlChange(section.section_id, key, value)}
                brandLogos={brandLogos}
              />
            );
          })
        ) : (
          <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: 16 }}>
            No schema found for {section.component_id}
          </div>
        )}

        {/* Host-styled components have no Section Styling: the page's own CSS styles them. */}
        {hostStyled && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4 }}>
            Uses this page’s own fonts, sizes and colours.
          </div>
        )}

        {/* Universal section styling — always at the bottom */}
        {!hostStyled && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(15,23,42,0.08)' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Section Styling
          </div>

          {/* Text colour */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', flexShrink: 0, width: 72 }}>Text Color</label>
            <input
              type="color"
              value={section.controls.section_text_color || '#000000'}
              onChange={e => onControlChange(section.section_id, 'section_text_color', e.target.value)}
              style={{ padding: 2, height: 28, width: 44, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
            />
            {section.controls.section_text_color && (
              <button onClick={() => onControlChange(section.section_id, 'section_text_color', '')}
                style={{ border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', cursor: 'pointer', padding: '3px 8px', fontSize: '0.7rem', color: '#64748b' }}>
                Reset
              </button>
            )}
          </div>

          {/* Button colour */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', flexShrink: 0, width: 72 }}>Button</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Bg</span>
                <input
                  type="color"
                  value={section.controls.section_button_color || '#1a365d'}
                  onChange={e => onControlChange(section.section_id, 'section_button_color', e.target.value)}
                  style={{ padding: 2, height: 28, width: 36, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Text</span>
                <input
                  type="color"
                  value={section.controls.section_button_text_color || '#ffffff'}
                  onChange={e => onControlChange(section.section_id, 'section_button_text_color', e.target.value)}
                  style={{ padding: 2, height: 28, width: 36, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer' }}
                />
              </div>
              {(section.controls.section_button_color || section.controls.section_button_text_color) && (
                <button
                  onClick={() => { onControlChange(section.section_id, 'section_button_color', ''); onControlChange(section.section_id, 'section_button_text_color', ''); }}
                  style={{ border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', cursor: 'pointer', padding: '3px 8px', fontSize: '0.7rem', color: '#64748b', marginLeft: 'auto' }}
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Text size */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', flexShrink: 0, width: 72 }}>Text Size</label>
            <select
              value={section.controls.section_text_size || 'md'}
              onChange={e => onControlChange(section.section_id, 'section_text_size', e.target.value)}
              style={{ fontSize: '0.75rem', border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 6px', background: '#fff', color: '#475569', cursor: 'pointer' }}
            >
              <option value="sm">Small (80%)</option>
              <option value="md">Normal</option>
              <option value="lg">Large (125%)</option>
              <option value="xl">XL (150%)</option>
            </select>
          </div>

          {/* Background mode toggle */}
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 6 }}>Background</label>
            <div style={{ display: 'flex', gap: 4 }}>
              {(['default', 'solid', 'gradient'] as const).map(mode => {
                const active = (section.controls.section_bg_mode || 'default') === mode;
                return (
                  <button
                    key={mode}
                    onClick={() => onControlChange(section.section_id, 'section_bg_mode', mode === 'default' ? '' : mode)}
                    style={{
                      flex: 1, padding: '5px 0', fontSize: '0.7rem', fontWeight: 600,
                      border: active ? '1.5px solid #2177FF' : '1px solid #e2e8f0',
                      borderRadius: 5, cursor: 'pointer',
                      background: active ? 'rgba(33,119,255,0.08)' : '#f8fafc',
                      color: active ? '#2177FF' : '#64748b',
                      textTransform: 'capitalize',
                    }}
                  >
                    {mode}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Solid colour */}
          {(section.controls.section_bg_mode || 'default') === 'solid' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <input
                type="color"
                value={section.controls.section_bg_color || '#ffffff'}
                onChange={e => onControlChange(section.section_id, 'section_bg_color', e.target.value)}
                style={{ padding: 2, height: 32, width: 44, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
              />
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                {section.controls.section_bg_color || 'Pick a colour'}
              </span>
              {section.controls.section_bg_color && (
                <button onClick={() => onControlChange(section.section_id, 'section_bg_color', '')}
                  style={{ border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', cursor: 'pointer', padding: '3px 8px', fontSize: '0.7rem', color: '#64748b', marginLeft: 'auto' }}>
                  Reset
                </button>
              )}
            </div>
          )}

          {/* Gradient colours + direction */}
          {(section.controls.section_bg_mode || 'default') === 'gradient' && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', marginBottom: 3 }}>Start</label>
                  <input
                    type="color"
                    value={section.controls.section_gradient_from || '#1a365d'}
                    onChange={e => onControlChange(section.section_id, 'section_gradient_from', e.target.value)}
                    style={{ width: '100%', height: 32, padding: 2, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', marginBottom: 3 }}>End</label>
                  <input
                    type="color"
                    value={section.controls.section_gradient_to || '#2563eb'}
                    onChange={e => onControlChange(section.section_id, 'section_gradient_to', e.target.value)}
                    style={{ width: '100%', height: 32, padding: 2, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', marginBottom: 3 }}>Direction</label>
                <select
                  value={section.controls.section_gradient_direction || '135deg'}
                  onChange={e => onControlChange(section.section_id, 'section_gradient_direction', e.target.value)}
                  style={{ width: '100%', fontSize: '0.75rem', border: '1px solid #e2e8f0', borderRadius: 4, padding: '5px 6px', background: '#fff', color: '#475569', cursor: 'pointer', boxSizing: 'border-box' }}
                >
                  <option value="135deg">Diagonal ↘ (135°)</option>
                  <option value="45deg">Diagonal ↗ (45°)</option>
                  <option value="to right">Left → Right</option>
                  <option value="to bottom">Top → Bottom</option>
                  <option value="90deg">Top → Bottom (90°)</option>
                  <option value="180deg">Bottom → Top (180°)</option>
                </select>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default PropertiesPanel;
